/**
 * ChartToolbar — the chart group's own header row: how long one bar is, how many
 * of them are on screen, and what is being charted.
 *
 *     ┌──────────────────────────────────────────────────────────┐
 *     │ [1] [1D] [4H] [15m] [▾] [150] [date]  ☆ AAPL - Apple Inc. │
 *     └──────────────────────────────────────────────────────────┘
 *
 * **"New plan" was here and is not any more.** It was the third way to start
 * one, beside the journal popover's own **New** and the drag on the chart that
 * defines entry and stop together — and the weakest of the three, because it
 * armed a plan from a row that shows no plans, so nothing came back to say it
 * had worked. The two that remain are each beside what they act on.
 *
 * ## Why the bar count is in this cluster
 *
 * "How many bars, at what interval" is one thought, so it is one row of
 * controls. The count is `ChartGroup.visibleBars` and it sets the **X axis
 * window** — it is not `barsRange`, which is how many bars the server sends, and
 * keeping them apart is the whole point: moving the window over bars that are
 * already loaded must never cost a request. See `lib/chart-window.ts`.
 *
 * It commits **on every keystroke**, unlike the interval picker's count box
 * beside it, and the difference is the cost of being wrong. An interval commits
 * a data request, so `4` on the way to `45` would fetch a chart nobody asked
 * for; a visible-bar count writes a range onto an axis that already exists, and
 * a moment at 3 bars on the way to 300 costs a repaint. The same line the
 * indicator picker draws between its parameters (behind an Apply) and its style
 * controls (applied on change).
 *
 * The date box beside it **centres** the chart on a date — context either side,
 * because a date you went looking for is one you want to see around. It is a
 * transient action and not state (D8): nothing here remembers it, and a workspace
 * never records that somebody once looked at March.
 *
 * ## Two controls, no captions
 *
 * "Symbol" and "Frequency" used to sit in front of their controls. They cost a
 * third of the row and told a reader nothing that `AAPL - Apple Inc.` and a row
 * of intervals do not say for themselves — this is a chart, and those are the
 * two things a chart has. The symbol is centred against the row rather than
 * pushed along by the interval picker, because it is the title of what is on
 * screen and a title that drifts with the width of its neighbour reads as an
 * accident. The mirror spacer on the right is what centres it; it yields at
 * narrow widths rather than overlapping the picker.
 *
 * The captions are gone but the accessible names are not: the symbol box keeps
 * an off-screen `<label htmlFor>`, or the combobox would reach a screen reader
 * unnamed — and once its dialog is open there are two comboboxes on the page.
 *
 * ## The Symbol control
 *
 * `<SymbolSearch>` opens its own search dialog and calls `onSymbol` on Enter and
 * on selection. The plain input it replaced dispatched on every keystroke, and
 * `symbol` is part of `ChartGroupView`'s `structuralKey` — so typing `AAPL` used
 * to cost four WebGL teardowns and four refetches to arrive at one chart. What
 * comes back is always a bare ticker, even though the box displays
 * `AAPL - Apple Inc.`; 220px, not the 96 a ticker needs, because a width sized
 * to `AAPL` would ellipsis away the half that made the label worth showing.
 *
 * ## Why chart type is not in this row
 *
 * `<ChartTypeButton>` lives in this file — it is the third chart-group control —
 * but it renders in the **vertical toolbar strip** beside the screener, where
 * `App` mounts it above the screener's own two buttons. Symbol and interval say
 * what is being charted; chart type says how it is drawn, which is the same kind
 * of statement as the buttons already in that strip, and a fourth `<select>`
 * competing with the symbol for the middle of the row was the worse home for it.
 *
 * NOTE: symbol + interval edits change the model and header labels only. Live
 * data fetch on those changes arrives with the transport layer (see the TODO in
 * ChartGroupView). Chart-type edits visibly reshape the price plot.
 *
 * Colours are `var(--qf-*)` only — chrome inherits from the shell root.
 */

import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import type { Frequency } from "@qufolio/model";

import { IntervalPicker } from "./IntervalPicker";
import { SymbolSearch } from "./SymbolSearch";
import { ChartListStar, STAR_ROOM, chartStarOffered } from "./ChartListStar";
import { ChartSymbolLock, LOCK_ROOM, LockNotice } from "./ChartSymbolLock";
import { ChartTypeIcon, GridToolbarButton } from "./GridToolbarButton";
import { ChartLiveBadge } from "./LiveBadge";
import {
  fullSymbolWidth,
  showsTickerOnly,
  toolbarFold,
  type ToolbarFold,
} from "../lib/toolbar-fold";
import { parseCount } from "../lib/interval-mru";
import { symbolLabel, useSymbolName } from "../lib/symbol-index";
import { formatComposite, isComposite } from "../lib/composite";
import type { ChartTypeChoice } from "../lib/chart-group";
import type { MarketCalendar } from "../api/calendar";
import { defaultDate, pickerRange, snapToTradingDay } from "../lib/trading-day";
import { useTheme } from "./theme";

export interface ChartToolbarProps {
  symbol: string;
  frequency: Frequency;
  onSymbol: (symbol: string) => void;
  onFrequency: (frequency: Frequency) => void;
  /** Bars on screen — `ChartGroup.visibleBars`, with the default already filled in. */
  visibleBars: number;
  onVisibleBars: (bars: number) => void;
  /** Centre the chart on a date. `yyyy-mm-dd`; transient, never stored. */
  onGoToDate: (date: string) => void;
  /** Market closures and the day the box should open on. */
  calendar: MarketCalendar;
  /**
   * The loaded bars' dates, which bound the picker. Optional: until the chart
   * publishes them the picker is bounded above by the last trading day and left
   * open below, which is honest — an invented floor would hide real history.
   */
  barDates?: readonly string[];
  /**
   * Open the app's symbol search for *this* chart, seeded with `query`.
   *
   * Threaded rather than owned, because the dialog is not per-chart: there is
   * one of it, the app renders it, and the caller records which chart the pick
   * belongs to. Every toolbar used to render its own, which is what made a
   * letter typed at the page land on an arbitrary one of them.
   */
  onOpenSearch?: (query: string, select: boolean) => void;
  /**
   * The picture-in-picture toggle, and whether it is currently on.
   *
   * `undefined` when the layout has more than two charts: past two, a cell is
   * small enough that a panel covering a corner costs more than it shows. The
   * key is **disabled with the reason on hover** rather than removed — a control
   * that vanishes teaches nothing, and the reason is the whole content of the
   * rule.
   */
  pip?: { on: boolean; onToggle: () => void } | undefined;
  /**
   * Put this chart full screen, or bring it back. Absent means not offered.
   * State is `App`'s — it has to hide the tab strip and every other section.
   */
  fullscreen?:
    | {
        on: boolean;
        onToggle: () => void;
        /**
         * ‹ n/N › beside ⛶, in full screen on a layout of two or more charts:
         * the next chart in Z order, wrapping. See `lib/fullscreen-cycle.ts`.
         */
        cycle?: { position: number; count: number; onStep: (step: 1 | -1) => void };
      }
    | undefined;
  /**
   * The chart this row belongs to, for its LIVE / DELAYED / CLOSED badge left
   * of ⛶ (owner, 2026-09-29). The chart publishes it (`lib/live-badge.ts`);
   * absent, or a daily chart, draws nothing.
   */
  chartGroupId?: string;
  /**
   * The symbol lock (owner, 2026-10-06): 🔒 before the symbol when locked, 🔓
   * on hover when not, and `notice` — set by `App` when it refused a symbol
   * change on this chart — draws "DIA is locked · 🔓 Unlock" under the box.
   * Absent means no lock is offered, and no room is made for one.
   */
  lock?: {
    locked: boolean;
    onToggle: (locked: boolean) => void;
    notice?: boolean;
  };
  /**
   * **Train** — a case for QueVee Voice from this chart (`docs/plans/quevee-voice.md`
   * §6). Absent for everyone who may not train a voice, which is everyone but a
   * platform admin for now. Per chart, unlike the data key: the case is *this*
   * chart's symbol, interval and picture.
   */
  train?: { onOpen: () => void };
  /**
   * **The data key is not here any more** — it is one key in the vertical strip,
   * and it aims at the *active* chart rather than at the row it sits in.
   *
   * It was a `⊞` on every toolbar, which put one key per chart in front of a
   * panel there was only ever one of: three charts meant three keys claiming to
   * open the same thing, and clicking a second one moved the panel rather than
   * opening anything. A click now also takes the screener's slot, which is a
   * layout-wide effect and has no business being offered by one chart's row.
   */
}

export function ChartToolbar({
  symbol,
  frequency,
  onSymbol,
  onFrequency,
  visibleBars,
  onVisibleBars,
  onGoToDate,
  calendar,
  barDates = [],
  onOpenSearch,
  pip,
  fullscreen,
  chartGroupId,
  lock,
  train,
}: ChartToolbarProps) {
  const symbolId = useId();
  // **Counted, not eyeballed.** The symbol is centred by a mirror spacer — both
  // sides take an equal share of the free space — so a key added after it pushes
  // it left by half that key's width. This puts the same width back on the other
  // side. The row is a fixed strip with no `overflow` to fall back on, which is
  // why adding to it is a sizing change rather than a decoration.
  //
  // One term, not two: the data key left this row for the vertical strip, and
  // the count has to lose it with it or the symbol sits 16px off centre on every
  // chart — the mirror would still be reserving space for a key nobody renders.
  const starred = chartStarOffered(symbol);
  // The lock rides in the same padding, between the star and the label, so it
  // is counted the same way: room on both sides, taken back off the width.
  const locking = lock !== undefined && symbol !== "";
  const star = starred ? STAR_ROOM : 0;
  const lockRoom = locking ? LOCK_ROOM : 0;
  const inner = star + lockRoom;
  const [symbolHovered, setSymbolHovered] = useState(false);

  // **Measured, then folded** — see `lib/toolbar-fold.ts`. The row's own width,
  // which is its cell's: the children overflow it, they never widen it.
  const rowRef = useRef<HTMLDivElement>(null);
  const [fold, setFold] = useState<ToolbarFold>(0);
  useLayoutEffect(() => {
    const row = rowRef.current;
    if (!row) return;
    const measure = () => setFold(toolbarFold(row.clientWidth));
    measure();
    // jsdom has none; the full row is then what a test sees.
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(row);
    return () => observer.disconnect();
  }, []);
  // At the last fold the symbol stops being centred, so the mirror has nothing
  // to balance and the PIP key needs no counting.
  const centred = fold < 3;
  // **From fold 2 the box grows into the free space** (owner, 2026-10-06: at
  // 4×3 the star and the lock left "X.." in a fixed 150px box between two empty
  // strips). Centred still at fold 2 — the picker's side, the box and the mirror
  // all grow by equal shares, so the middle stays the middle.
  const grows = fold >= 2;
  // Measured so a box that still cannot fit a name shows the ticker alone
  // rather than truncating it — "XLK", never "X..".
  const boxRef = useRef<HTMLSpanElement>(null);
  const [boxWidth, setBoxWidth] = useState(0);
  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => setBoxWidth(box.clientWidth));
    observer.observe(box);
    return () => observer.disconnect();
  }, []);
  const tickerOnly = showsTickerOnly(fold, boxWidth, inner);
  // **The full row's box fits its label, and gives way when the row is full**
  // (owner, 2026-10-09: "RELY - Re…" and "TWLO - Tw…" in 220px beside empty
  // strips). The label is read here, through the same hook the box uses, so
  // this re-renders when the company name arrives — measuring the input's value
  // on the first render saw only the ticker. The width it asks for is the whole
  // label; the box is `flex: 0 1 auto` with a 220 floor, so it shrinks into
  // whatever the date box and LIVE leave rather than past them.
  const name = useSymbolName(symbol);
  const fullLabel = isComposite(symbol) ? formatComposite(symbol) : symbolLabel(symbol, name);
  const [textPx, setTextPx] = useState(0);
  useLayoutEffect(() => {
    if (fold >= 2) return;
    const input = document.getElementById(symbolId);
    if (!(input instanceof HTMLInputElement)) return;
    setTextPx(measureText(fullLabel, getComputedStyle(input).font));
  }, [fold, fullLabel, symbolId]);
  const symbolWidth = fold < 2 ? fullSymbolWidth(textPx, inner) : SYMBOL_WIDTH[fold];
  const keys = pip && centred ? KEY_WIDTH + KEY_GAP : 0;
  const dateBox = <GoToDateBox onGo={onGoToDate} calendar={calendar} barDates={barDates} />;
  const barsBox = <VisibleBarsBox value={visibleBars} onChange={onVisibleBars} />;

  return (
    <div
      ref={rowRef}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "0.75rem",
        padding: "0.4rem 1rem",
        borderBottom: "1px solid var(--qf-border)",
        background: "var(--qf-background)",
      }}
    >
      <div
        style={{
          // Basis 0 with the mirror spacer is what centres the symbol against
          // the *row*: both sides take an equal share of the free space, so the
          // middle is the middle however wide the picker is.
          flex: centred ? "1 1 0" : "0 0 auto",
          display: "flex",
          alignItems: "center",
          gap: "0.4rem",
          justifyContent: "flex-start",
          // …but never below what is in it. `minWidth: 0` was safe while this
          // held only the interval picker; with three controls in it, a
          // half-width chart gave this side less space than its contents and
          // they painted straight over the symbol box — 90px of overlap on an
          // 800px column, invisible because the symbol's text is centred inside
          // an input that was underneath the date box. The spacer yields
          // instead, which is what it is for.
          minWidth: "max-content",
        }}
      >
        <span style={{ width: keys, flex: `0 0 ${keys}px` }} aria-hidden />
        <IntervalPicker value={frequency} onChange={onFrequency} compact={fold >= 2} />
        {fold < 2 && barsBox}
        {fold === 0 ? (
          dateBox
        ) : (
          <FoldedKey label={fold >= 2 ? "Bars visible and go to date" : "Go to date"}>
            {fold >= 2 && barsBox}
            {dateBox}
          </FoldedKey>
        )}
      </div>

      {/*
        The label is a *sibling* with `htmlFor`, never a wrapper: the search
        dialog renders inside this control, and a click on a plain element within
        a `<label>` is forwarded to the labelled input — which would swallow the
        click that picks a row. Off-screen rather than `display: none`, which is
        skipped by accessibility tooling as well as by the eye.
      */}
      <label htmlFor={symbolId} style={offScreen}>
        Symbol
      </label>
      {/* The track-list star rides inside the box's left padding. The same room
          goes on the right so the centred text stays centred, and both come
          back off the width, so the box's outer size — and the counted row —
          does not move. See `ChartListStar`. */}
      {/* **At the last fold the box grows into the free space** (owner, 2026-10-06:
          five charts across showed "S.." and "^.." beside an empty strip). There
          the symbol is no longer centred, so nothing needs balancing: the box
          takes what the row leaves, from its 96px floor to the full row's 220px,
          and the star keeps only the left padding it actually sits in. Wider
          folds keep their fixed, centred width — the mirror spacer counts it. */}
      {/* The symbol lock sits in the same padding, between the star and the
          label (☆ 🔒 DIA), counted the same way — see `ChartSymbolLock`. Its
          room is held whether or not 🔓 is showing, so hovering moves nothing. */}
      <span
        ref={boxRef}
        onMouseEnter={() => setSymbolHovered(true)}
        onMouseLeave={() => setSymbolHovered(false)}
        style={{
          position: "relative",
          display: "inline-flex",
          ...(grows
            ? { flex: "1 1 auto", minWidth: SYMBOL_WIDTH[fold], maxWidth: SYMBOL_WIDTH[0] }
            : { flex: "0 1 auto", width: symbolWidth, minWidth: SYMBOL_WIDTH[fold] }),
        }}
      >
        <SymbolSearch
          id={symbolId}
          value={symbol}
          tickerOnly={tickerOnly}
          onCommit={onSymbol}
          onOpenSearch={onOpenSearch}
          style={
            fold >= 3
              ? {
                  width: "100%",
                  boxSizing: "border-box",
                  textAlign: "center",
                  fontWeight: 600,
                  ...(inner ? { paddingLeft: `calc(0.4rem + ${inner}px)` } : {}),
                }
              : fold === 2
                ? {
                    // Still centred, so the star's and lock's room goes on both
                    // sides, as at the wider folds.
                    width: "100%",
                    boxSizing: "border-box",
                    textAlign: "center",
                    fontWeight: 600,
                    ...(inner
                      ? {
                          paddingLeft: `calc(0.4rem + ${inner}px)`,
                          paddingRight: `calc(0.4rem + ${inner}px)`,
                        }
                      : {}),
                  }
              : {
                  // The box's width is the span's, which flexes — see `symbolWidth`.
                  width: "100%",
                  boxSizing: "border-box",
                  textAlign: "center",
                  fontWeight: 600,
                  ...(inner
                    ? {
                        paddingLeft: `calc(0.4rem + ${inner}px)`,
                        paddingRight: `calc(0.4rem + ${inner}px)`,
                      }
                    : {}),
                }
          }
        />
        <ChartListStar symbol={symbol} inset={lockRoom} />
        {locking && (
          <ChartSymbolLock
            symbol={symbol}
            locked={lock.locked}
            hovered={symbolHovered}
            starRoom={star}
            onToggle={lock.onToggle}
          />
        )}
        {locking && lock.notice && lock.locked && (
          <LockNotice symbol={symbol} onUnlock={() => lock.onToggle(false)} />
        )}
      </span>

      {/* Beside the symbol, because it is about the name written next to it: the
          PIP shows that symbol's higher timeframe. It is the only key left on
          this row — the data panel's went to the vertical strip, where one key
          for the layout matches the one panel there has always been. */}
      {pip && (
        <ToolbarKey
          label={pip.on ? "Hide the picture-in-picture panel" : "Show a picture-in-picture panel"}
          pressed={pip.on}
          onClick={() => pip.onToggle()}
        >
          ◱
        </ToolbarKey>
      )}

      {/* The mirror of the picker's side, and the only reason the symbol sits in
          the middle of the row rather than in the middle of what is left.

          The full-screen key lives *inside* it, pushed to the far right, so it
          needs no counting: both sides are `flex: 1 1 0` and take equal shares
          of the row whatever is in them, so the symbol does not move. Its
          `minWidth` is what keeps the key from painting over the symbol when a
          narrow chart squeezes the row.

          The LIVE badge sits left of ⛶ in the same box, so it needs no counting
          either; `max-content` keeps the pair clear of the symbol, and a narrow
          chart pushes the symbol left rather than overlapping, as the picker's
          side already does. */}
      <div
        style={{
          flex: centred ? "1 1 0" : "1 1 auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: KEY_GAP,
          minWidth: "max-content",
        }}
      >
        {/* In this box for ⛶'s reason: both sides take equal shares, so a key
            here moves nothing and needs no counting. "⏺ Train" on the full
            row, the dot alone once it folds. */}
        {train && (
          <button
            type="button"
            title="Train: save your read of this chart"
            aria-label="Train"
            onClick={train.onOpen}
            style={{ ...boxStyle, display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer", padding: "0 6px" }}
          >
            <span style={{ color: "var(--qf-negative)" }}>⏺</span>
            {fold === 0 && "Train"}
          </button>
        )}
        {chartGroupId && <ChartLiveBadge chartGroupId={chartGroupId} height={24} dotOnly={fold >= 1} />}
        {/* In this box for ⛶'s reason — no counting needed — and only in full
            screen, where the row is the width of the screen. */}
        {fullscreen?.on && fullscreen.cycle && (
          <>
            <ToolbarKey label="Previous chart (Alt+←)" pressed={false} onClick={() => fullscreen.cycle!.onStep(-1)}>
              ‹
            </ToolbarKey>
            <span
              aria-label={`Chart ${fullscreen.cycle.position} of ${fullscreen.cycle.count}`}
              style={{ fontSize: 12, color: "var(--qf-text-secondary)", minWidth: 28, textAlign: "center" }}
            >
              {fullscreen.cycle.position}/{fullscreen.cycle.count}
            </span>
            <ToolbarKey label="Next chart (Alt+→)" pressed={false} onClick={() => fullscreen.cycle!.onStep(1)}>
              ›
            </ToolbarKey>
          </>
        )}
        {fullscreen && (
          <ToolbarKey
            label={fullscreen.on ? "Exit full screen (Esc)" : "Full screen"}
            pressed={fullscreen.on}
            onClick={() => fullscreen.onToggle()}
          >
            ⛶
          </ToolbarKey>
        )}
      </div>
    </div>
  );
}

/**
 * A label's width in px, in the input's own font. One canvas for the page; jsdom
 * has no 2D context, and 0 there is the box at rest.
 */
let measureCanvas: HTMLCanvasElement | undefined;
function measureText(text: string, font: string): number {
  if (!text) return 0;
  measureCanvas ??= document.createElement("canvas");
  let ctx: CanvasRenderingContext2D | null = null;
  try {
    ctx = measureCanvas.getContext("2d");
  } catch {
    return 0;
  }
  if (!ctx) return 0;
  ctx.font = font;
  return ctx.measureText(text).width;
}

/**
 * The symbol box per fold: ticker and name, then less name, then the ticker
 * alone. The full row's 220 is its width *at rest* — `fullSymbolWidth` grows it
 * to fit a long name where the row has room.
 */
const SYMBOL_WIDTH: Record<ToolbarFold, number> = { 0: 220, 1: 220, 2: 150, 3: 96 };

/**
 * 📅 — what a folded row put away, one click off. A popover under the key,
 * closed by a click anywhere outside it; the boxes inside are the same
 * components the full row shows, so they behave identically.
 */
function FoldedKey({ label, children }: { label: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!boxRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [open]);
  return (
    <div ref={boxRef} style={{ position: "relative", display: "flex" }}>
      <ToolbarKey label={label} pressed={open} onClick={() => setOpen((v) => !v)}>
        📅
      </ToolbarKey>
      {open && (
        <div role="dialog" aria-label={label} style={FOLDED_POPOVER}>
          {children}
        </div>
      )}
    </div>
  );
}

const FOLDED_POPOVER: React.CSSProperties = {
  position: "absolute",
  top: "calc(100% + 4px)",
  left: 0,
  display: "flex",
  gap: 6,
  padding: 6,
  borderRadius: 4,
  background: "var(--qf-surface)",
  border: "1px solid var(--qf-border)",
  zIndex: 30,
};

/** One toolbar key. Sized to the boxes beside it rather than to the 34px strip's. */
const KEY_WIDTH = 26;
const KEY_GAP = 6;

/**
 * A square key on the symbol's row.
 *
 * Its own component rather than `GridToolbarButton`, which is 34px and sized to
 * the vertical strip: a 34px key beside a 24px interval box reads as a mistake,
 * and matching the row is what makes these look like part of it.
 */
function ToolbarKey({
  label,
  pressed,
  onClick,
  children,
}: {
  label: string;
  pressed: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  // The key's own rectangle used to travel with every click and hover: the data
  // panel was rendered by `App` and only this row knew where its key was. That
  // key is in the vertical strip now, and the PIP toggle — all that is left —
  // opens nothing that needs anchoring, so the measurement went with it.
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={pressed}
      onClick={onClick}
      style={{
        ...boxStyle,
        width: KEY_WIDTH,
        justifyContent: "center",
        cursor: "pointer",
        ...(pressed
          ? {
              background: "var(--qf-selection)",
              borderColor: "var(--qf-accent)",
              color: "var(--qf-accent)",
            }
          : {}),
      }}
    >
      {children}
    </button>
  );
}

/** The shared look of the two small boxes, so they read as one cluster. */
const boxStyle: React.CSSProperties = {
  font: "inherit",
  fontSize: "0.75rem",
  height: 24,
  padding: "0 0.35rem",
  borderRadius: 4,
  border: "1px solid var(--qf-border)",
  background: "var(--qf-surface)",
  color: "var(--qf-text)",
};

/**
 * How many bars are on screen.
 *
 * A **draft** string rather than the number itself, for the reason every count
 * box in this app keeps one: an input bound straight to a number cannot hold
 * "3" on the way to "30" without the parent snapping it back, and cannot be
 * emptied at all. Only a value that parses commits; anything else stays in the
 * box and changes nothing, so a half-typed count never reaches the axis.
 *
 * `parseCount` is the interval picker's, deliberately — "what a count box holds,
 * as a number, or null" is the same question, and a second copy of it would be a
 * second place for a leading zero to mean something different.
 */
export function VisibleBarsBox({
  value,
  onChange,
  width = 52,
}: {
  value: number;
  onChange: (bars: number) => void;
  /** The phone's bottom row is counted to the pixel — see `MobileApp`. */
  width?: number;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  // Cleared when the value arrives from elsewhere — a preset, a reload, a synced
  // twin — or the box would keep showing what was typed into it last while the
  // chart showed something else.
  useEffect(() => setDraft(null), [value]);

  const shown = draft ?? String(value);
  const parsed = parseCount(shown);

  return (
    <input
      value={shown}
      inputMode="numeric"
      aria-label="Bars visible"
      title="Bars visible — the X axis window. Does not reload data."
      onChange={(e) => {
        setDraft(e.target.value);
        const bars = parseCount(e.target.value);
        if (bars !== null) onChange(bars);
      }}
      style={{
        ...boxStyle,
        width,
        textAlign: "center",
        // The one signal a typed value went nowhere. Same treatment as the
        // interval picker's count box, which has the same failure to show.
        borderColor: parsed === null ? "var(--qf-negative)" : "var(--qf-border)",
      }}
    />
  );
}

/**
 * Type a date, the chart centres on it.
 *
 * A native `type="date"` rather than a text box and a parser: the box is not the
 * interesting part of this feature, and every locale's idea of `03/08` is a
 * different day. It hands back `yyyy-mm-dd` whatever it displays, which is the
 * one format `parseDateInput` has to read.
 *
 * `colorScheme` comes from the active theme rather than being hardcoded, because
 * the calendar glyph is drawn by the browser and nothing in `--qf-*` can reach
 * it — a light-scheme picker on a dark chart is a control you cannot see. It is
 * a mode, not a colour, so the no-colours-in-components rule is intact.
 */
export function GoToDateBox({
  onGo,
  calendar,
  barDates = [],
  width = 132,
  noteAbove = false,
  icon = false,
}: {
  onGo: (date: string) => void;
  calendar: MarketCalendar;
  barDates?: readonly string[];
  width?: number;
  /** Float the snap note above the box: on the phone the row sits on the tab bar. */
  noteAbove?: boolean;
  /**
   * A calendar key instead of the date (owner, 2026-10-13: the phone's row has
   * no room for the full date). The native input still does the work, laid
   * invisibly over the key so a tap lands on it; `showPicker` covers browsers
   * that open the picker only from the input's own glyph.
   */
  icon?: boolean;
}) {
  const theme = useTheme();
  // Shown, not just defaulted: an empty box reading `mm/dd/yyyy` says nothing
  // about where the chart is, and the box's job is to say exactly that.
  const [value, setValue] = useState(() => defaultDate(calendar));
  const [note, setNote] = useState<string | null>(null);

  // Back to the last trading day whenever the calendar arrives or rolls over —
  // the app loads before the fetch returns, so the first value is the offline
  // fallback and this is what replaces it.
  useEffect(() => {
    setValue(defaultDate(calendar));
    setNote(null);
  }, [calendar.lastTradingDay, calendar.today]);

  const range = pickerRange(barDates, calendar.lastTradingDay, calendar.today);

  return (
    <div style={{ position: "relative", display: "flex" }}>
      <input
        type="date"
        aria-label="Go to date"
        title="Go to date — centres the chart on it"
        value={value}
        {...range}
        onChange={(e) => {
          const asked = e.target.value;
          if (!asked) return;
          // A closed day can still be picked: `<input type="date">` supports
          // `min`/`max` and nothing else, so there is no way to grey out
          // individual holidays without hand-building a calendar popup. Snap
          // backwards and say why, rather than moving in silence or ignoring it.
          const { date, reason } = snapToTradingDay(asked, calendar.holidays);
          setValue(date);
          setNote(reason ?? null);
          onGo(date);
        }}
        onClick={
          icon
            ? (e) => {
                try {
                  e.currentTarget.showPicker?.();
                } catch {
                  // Not allowed here (no user activation, or unsupported) —
                  // the tap on the input itself still opens it on touch.
                }
              }
            : undefined
        }
        style={
          icon
            ? { position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0, colorScheme: theme.mode }
            : { ...boxStyle, width, colorScheme: theme.mode }
        }
      />
      {icon && (
        <span aria-hidden="true" style={{ ...boxStyle, ...ICON_KEY }}>
          <CalendarGlyph />
        </span>
      )}
      {note && (
        <span
          role="status"
          style={noteAbove ? { ...SNAP_NOTE, top: "auto", bottom: "calc(100% + 4px)" } : SNAP_NOTE}
        >
          {note}
        </span>
      )}
    </div>
  );
}

const ICON_KEY: React.CSSProperties = {
  width: 32,
  height: 28,
  padding: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 6,
  color: "var(--qf-text-secondary)",
  // Painted above the invisible input, so a tap must pass through to it.
  pointerEvents: "none",
};

function CalendarGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3">
      <rect x="2" y="3" width="12" height="11" rx="1.5" />
      <path d="M2 6.5h12M5 1.5v3M11 1.5v3" />
    </svg>
  );
}

/**
 * Why the date moved. Floats clear of the row rather than widening it — the
 * toolbar is a fixed strip and a message that reflowed it would shift the
 * symbol out from under the pointer that just used this control.
 */
const SNAP_NOTE: React.CSSProperties = {
  position: "absolute",
  top: "calc(100% + 4px)",
  left: 0,
  whiteSpace: "nowrap",
  fontSize: 11,
  padding: "3px 7px",
  borderRadius: 4,
  background: "var(--qf-surface)",
  border: "1px solid var(--qf-border)",
  color: "var(--qf-text-secondary)",
  zIndex: 20,
};

/**
 * Visually hidden, still announced. `clip-path` rather than `width: 0`, which
 * some engines treat as "not rendered" and drop from the accessibility tree.
 */
const offScreen: React.CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  overflow: "hidden",
  clipPath: "inset(50%)",
  whiteSpace: "nowrap",
};

/** Price-appropriate renderable types, and how they are spelled to a person. */
const CHART_TYPES: { type: ChartTypeChoice; label: string }[] = [
  { type: "candlestick", label: "Candlestick" },
  { type: "ohlc", label: "OHLC" },
  { type: "line", label: "Line" },
  { type: "mountain", label: "Mountain" },
  // Heikin-Ashi is not a renderable — it re-forms the bars, and lives on
  // `ChartGroup.barSource` rather than `Plot.type`. It is in this list because
  // that is where a person looks for it, and because the five are exclusive: a
  // chart draws one kind of price. `setChartTypeChoice` writes whichever field
  // the choice belongs to.
  { type: "heikinAshi", label: "Heikin-Ashi" },
];

export interface ChartTypeButtonProps {
  /** What the price currently looks like — a plot type, or the bar source. */
  value: ChartTypeChoice | undefined;
  onChange: (choice: ChartTypeChoice) => void;
}

/**
 * Chart type, as one icon in the vertical toolbar strip.
 *
 * Five options is far too many for a cycle-on-click button — nobody wants to
 * click four times past OHLC — so it opens a small menu, positioned the same
 * way every popover in this app is: `fixed`, measured off its trigger. The strip
 * is 34px wide and lives inside the grid's clipping context, which an
 * absolutely-positioned menu would be cut off by.
 */
export function ChartTypeButton({ value, onChange }: ChartTypeButtonProps) {
  const [open, setOpen] = useState(false);
  // Anchored to the wrapper, not the button: `GridToolbarButton` renders the
  // `<button>` and does not forward a ref, and the two have the same box.
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const active = CHART_TYPES.find((c) => c.type === value);

  return (
    <div ref={anchorRef} style={{ position: "relative", display: "flex" }}>
      <GridToolbarButton
        label={`Chart type: ${active?.label ?? value}`}
        active={open}
        onClick={() => setOpen((v) => !v)}
      >
        <ChartTypeIcon />
      </GridToolbarButton>
      {open && (
        <Menu anchorRef={anchorRef} onClose={() => setOpen(false)}>
          <div role="listbox" aria-label="Chart type" style={{ padding: 4 }}>
            {CHART_TYPES.map((choice) => (
              <button
                key={choice.type}
                type="button"
                role="option"
                aria-selected={choice.type === value}
                onClick={() => {
                  onChange(choice.type);
                  setOpen(false);
                }}
                style={{
                  display: "block",
                  width: "100%",
                  font: "inherit",
                  fontSize: 12,
                  padding: "5px 10px",
                  border: 0,
                  borderRadius: 4,
                  background: choice.type === value ? "var(--qf-selection)" : "transparent",
                  color: choice.type === value ? "var(--qf-accent)" : "var(--qf-text)",
                  cursor: "pointer",
                  textAlign: "left",
                  whiteSpace: "nowrap",
                }}
              >
                {choice.label}
              </button>
            ))}
          </div>
        </Menu>
      )}
    </div>
  );
}

/**
 * The same `fixed` popover `GroupBar.tsx` and `IntervalPicker.tsx` use, and for
 * the same reason: an absolutely-positioned menu is clipped by the nearest
 * ancestor with `overflow: hidden`, and everything here sits inside one. It
 * opens to the **left** of its trigger, because the strip it hangs off is
 * pinned between the chart and the screener and a menu to the right would cover
 * the grid.
 */
function Menu({
  anchorRef,
  children,
  onClose,
}: {
  anchorRef: React.RefObject<HTMLElement | null>;
  children: React.ReactNode;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    const menu = ref.current;
    if (!anchor || !menu) return;

    const rect = anchor.getBoundingClientRect();
    const height = menu.offsetHeight;
    const width = menu.offsetWidth;

    const below = rect.bottom + 4;
    const flip = below + height > window.innerHeight && rect.top - height - 4 > 0;
    const top = flip ? rect.top - height - 4 : below;
    const left = Math.max(4, Math.min(rect.left - width - 6, window.innerWidth - width - 4));

    setPos({ top, left });
  }, [anchorRef, children]);

  useEffect(() => {
    const onDown = (event: PointerEvent) => {
      if (!ref.current?.contains(event.target as Node)) onClose();
    };
    document.addEventListener("pointerdown", onDown, true);
    const onScrollOrResize = () => onClose();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      style={{
        position: "fixed",
        top: pos?.top ?? 0,
        left: pos?.left ?? 0,
        visibility: pos ? "visible" : "hidden",
        zIndex: 1000,
        background: "var(--qf-surface)",
        border: "1px solid var(--qf-border)",
        borderRadius: 6,
        boxShadow: "0 6px 20px rgba(0,0,0,0.35)",
        minWidth: 130,
      }}
    >
      {children}
    </div>
  );
}
